<template>
  <div>

    <!-- Sterbedatum  -->

    <div class="flex-100 px-2 mb-1" v-if="!isImport">
      <validation-provider tag="div"
                           class="flex-gt-sm-100 layout-column"
                           :name="path + '.sterbedatum'"
                           :ref="path + '.sterbedatum'"
                           :rules="{german_date: true, date_not_in_future: true, required: true, death_date_must_be_latest: [patientId, true]}"
                           v-slot="{ errors }">
        <div class="flex-100 layout-row">
          <lx-datepicker
            :color="compColor.textInput"
            :auto-focus="!model._id"
            :name="path + '.sterbedatum'"
            :label="$t('FORMS_DIVERSE.MODULE_DEATH_DATE') + ' *'"
            :disabled="isDisabled"
            dense
            class="flex-33 flex-100"
            :error="errors[0]"
            :hide-details="errors[0] && errors[0].includes('<span')"
            v-model="model[path].sterbedatum"
          ></lx-datepicker>

          <!-- Diagnose  -->

          <div class="flex-gt-sm-66 flex-100 ml-4">
            <validation-provider tag="div"
                                 name="subj_id"
                                 ref="subj_id"
                                 class="layout-row"
                                 rules="required"
                                 v-slot="{ errors }">
              <v-select
                :items="diagnoses"
                :label="$t('FORMS_DIVERSE.MODULE_DEATH_DIAGNOSIS_LABEL') + ' *'"
                name="subj_id"
                :color="compColor.primeColor"
                :dark="compColor.isInDarkMode"
                :disabled="diagnoses.length === 1 || isFormDisabled()"
                dense
                @change="setRegisterFormId"
                outlined
                item-value="subj_id"
                class="my-0"
                :hide-details="!errors || errors.length <= 0"
                v-model="model.subj_id"
                :error-messages="errors[0]"
                :menu-props="{ bottom: true, offsetY: true }">
                <template v-slot:selection='{ item }'>
                  <span v-if="!compColor.isInDarkMode" :style="{ 'color': diagnoses.length === 1 || isFormDisabled() ? 'rgba(0, 0, 0, 0.38)' : '#445a64', 'font-size': denseFields ? '14.4px !important' : '16px !important'}">
                    {{ formatTextOfDiagnosisSelection(item) }}</span>
                  <span v-if="compColor.isInDarkMode" :style="{ 'color': diagnoses.length === 1 || isFormDisabled() ? 'rgba(255, 255, 255, 0.5)' : 'white', 'font-size': denseFields ? '14.4px !important' : '16px !important'}">
                    {{ formatTextOfDiagnosisSelection(item) }}</span>
                </template>
                <template v-slot:item="{ item, on, attrs }">
                  <v-list-item v-on="on"
                               :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                    <v-list-item-content class="py-0">
                      <label style="cursor:pointer;"
                             :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                             class="lighten-4 text--darken-2">{{ formatTextOfDiagnosisSelection(item) }}</label>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-select>

              <v-tooltip bottom open-delay="1000" max-width="800">
                <template v-slot:activator="{ on }">
                  <div v-on="on" class="layout-row layout-align-start-start ml-3" :class="{'mt-2': denseFields, 'mt-3': !denseFields}"
                       style="cursor: pointer;">
                    <v-icon :color="compColor.prime" medium>info</v-icon>
                  </div>
                </template>

                <p style="text-align: justify;" class="my-1">{{ $t('FORMS_DIVERSE.MODULE_DEATH_DIAGNOSIS_HINT') }}</p>
              </v-tooltip>
            </validation-provider>
          </div>
        </div>

        <!-- Show complex error message only if death_date_must_be_latest is triggered -->

        <lx-alert-box v-show="errors[0] && errors[0].includes('<span')" color="red" class="my-3 py-2 layout-column">
          <span>{{ $t('FORMS_DIVERSE.MODULE_DEATH_ERROR_MESSAGE_PART_1') }}</span>
          <div class="my-1 ml-4" v-html="errors[0]"></div>
          <span>{{ $t('FORMS_DIVERSE.MODULE_DEATH_ERROR_MESSAGE_PART_2') }}</span>
        </lx-alert-box>
      </validation-provider>
    </div>

    <!-- Tod tumorbedingt? -->

    <div class="flex-100 px-2 mb-2">
      <validation-provider tag="div"
                           :name="path + '.tod_tumorbedingt'"
                           :ref="path + '.tod_tumorbedingt'"
                           :rules="{required: true}"
                           class="layout-column"
                           v-slot="{ errors }">
        <fake-input-frame :label="$t('FORMS_DIVERSE.MODULE_DEATH_DEPENDS_ON_TUMOR') + ' *'"
                          :disabled="isDisabled"
                          :errors="errors.length > 0">

          <div class="px-4 pb-2 flex">
            <v-radio-group :dense="denseFields" v-model="model[path].tod_tumorbedingt" row hide-details :error-messages="errors[0]" class="ma-0 pa-0">
              <v-radio :label="$t('FORMS_DIVERSE.RADIO_GROUP_JNU.YES')"
                       value="J" :disabled="isDisabled" :color="compColor.primeColor" :dark="compColor.isInDarkMode">
              </v-radio>
              <v-radio :label="$t('FORMS_DIVERSE.RADIO_GROUP_JNU.NO')"
                       value="N" :disabled="isDisabled" :color="compColor.primeColor" :dark="compColor.isInDarkMode">
              </v-radio>
              <v-radio :label="$t('FORMS_DIVERSE.RADIO_GROUP_JNU.UNKNOWN')"
                       value="U" :disabled="isDisabled" :color="compColor.primeColor" :dark="compColor.isInDarkMode">
              </v-radio>
            </v-radio-group>
          </div>
        </fake-input-frame>

        <!-- Anzeige Fehlermeldung  -->
        <span class="error--text" style="line-height:12px;font-size:12px;margin-top: 3px;margin-left:6px;">{{ errors[0] }}</span>
      </validation-provider>
    </div>

    <!-- Todesursachen  -->

    <div class="flex-100 px-2 mb-6 mt-4">
      <fake-input-frame :label="$t('FORMS_DIVERSE.MODULE_DEATH_CAUSE_OF_DEATH')"
                        :disabled="isDisabled">
        <div class="layout-column flex px-2 pb-2">
          <div v-bind:style="{'z-index': '4'}" class="layout-row flex layout-align-start-center pa-1">
            <lx-btn :disabled="isDisabled"
                    icon="add_circle"
                    :color="compColor.primeColor"
                    :iconColor="'white'"
                    :label="$t('FORMS_DIVERSE.MODULE_DEATH_ADD_CAUSE_OF_DEATH')"
                    :tooltip="$t('FORMS_DIVERSE.MODULE_DEATH_ADD_CAUSE_OF_DEATH')"
                    iconSize="21"
                    :width="'auto'"
                    :height="35"
                    @click="addCauseOfDeath()">
            </lx-btn>
          </div>

          <div :key="index" v-for="(item, index) in model[path].todesursachen"
               class="layout-row pa-0 ma-1"
               :class="{'elevation-5': compColor.isInDarkMode, 'grey lighten-3': !compColor.isInDarkMode}"
               style="border-radius:4px;"
               :style="{'border': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">

            <div class="layout-row layout-wrap flex px-3">

              <!-- ICD10-Code der Todesursache  -->

              <div class="flex-gt-sm-33 flex-100 px-2 py-3">
                <validation-provider tag="div"
                                     :name="path + '.todesursachen.' + index + '.code'"
                                     :ref="path + '.todesursachen.' + index + '.code'"
                                     rules="required|min:2"
                                     v-slot="{ errors }">
                  <lx-autocomplete
                    :label="$t('FORMS_DIVERSE.ICD_CODE') + ' *'"
                    v-model="item.code"
                    :error="errors[0]"
                    @search-method="getDataForAutocomplete"
                    @input="setDiagnosis"
                    :disabled="isDisabled"
                    dense
                    :index="index"
                    :hide-details="!errors || errors.length <= 0"
                    :results="resultSearchModel">
                    <template v-slot="slotProps">
                      <div class="layout-row">
                        <div style="width: 100px;border-radius: 4px;" class="mr-2 px-2 white lighten-2 layout-column layout-align-center-center">
                          <h3>
                            <lx-text-highlight :text="slotProps.lineItem.code" :search-string="slotProps.searchText" :color="compColor.prime"></lx-text-highlight>
                          </h3>
                        </div>
                        <div class="flex">
                        <span class="body-2">
                          <lx-text-highlight :text="slotProps.lineItem.description" :search-string="slotProps.searchText" :color="compColor.prime"></lx-text-highlight>
                        </span>
                        </div>
                      </div>
                    </template>
                  </lx-autocomplete>
                </validation-provider>
              </div>

              <!-- ICD10-Version der Todesursache  -->

              <div class="flex-gt-sm-66 flex-100 px-2 pt-3">
                <validation-provider tag="div"
                                     :name="path + '.todesursachen.' + index + '.version'"
                                     :ref="path + '.todesursachen.' + index + '.version'"
                                     :rules="{required: !!(item && item.code)}"
                                     v-slot="{ errors }">
                  <v-select
                    :items="Primaertumor_ICD_VersionTypes"
                    label="ICD10 Version *"
                    :name="path + '.todesursachen.' + index + '.version'"
                    :color="compColor.primeColor"
                    :dark="compColor.isInDarkMode"
                    :disabled="isDisabled"
                    :persistent-hint="true"
                    :hint="$t('FORMS_DIVERSE.CODE_VERSION_HINT')"
                    dense
                    @change="resetICD10Code(index)"
                    outlined
                    class="my-0"
                    v-model="item.version"
                    :error-messages="errors[0]"
                    :menu-props="{ bottom: true, offsetY: true }">
                    <template v-slot:item="{ item, on, attrs }">
                      <v-list-item v-on="on"
                                   :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                        <v-list-item-content class="py-0">
                          <label style="cursor:pointer;"
                                 :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                 class="lighten-4 text--darken-2">{{ item.text }}</label>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </validation-provider>
              </div>

              <!-- Freitext Todesursache  -->

              <div class="flex-gt-sm-100 flex-100 px-2 py-3">
                <validation-provider tag="div"
                                     :name="path + '.todesursachen.' + index + '.description'"
                                     :ref="path + '.todesursachen.' + index + '.description'"
                                     rules=""
                                     v-slot="{ errors }">
                  <v-text-field
                    :label="$t('FORMS_DIVERSE.ICD_CODE_TEXT')"
                    :color="compColor.primeColor"
                    :dark="compColor.isInDarkMode"
                    v-model="item.description"
                    disabled
                    :hide-details="!errors || errors.length <= 0"
                    dense
                    outlined
                    :error-messages="errors[0]"
                    class="ma-0"
                  ></v-text-field>
                </validation-provider>
              </div>
            </div>

            <div class="layout-column layout-align-center-center px-3"
                 :class="{'grey lighten-2': !compColor.isInDarkMode}"
                 :style="{'border-left': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">
              <lx-btn @click="deleteItem(path + '.todesursachen', index, path + '.todesursachen.' + index + '.description')"
                      :color="compColor.primeColor"
                      :iconColor="'white'"
                      iconSize="21"
                      :width="35"
                      :height="35"
                      :disabled="isDisabled"
                      icon="delete_forever"></lx-btn>
            </div>
          </div>
        </div>
      </fake-input-frame>
    </div>
  </div>
</template>

<script>
import utils from '../../../common/utils'
import metaData from '../shared/formsMetaData'
import LxAutocomplete from '../../lx_auto_complete/LxAutocomplete'
import LxTextHighlight from '../../lx_text_highlighter/LxTextHighlight'

export default {
  name: "Tod",
  mixins: [utils,metaData],
  components: {
    LxAutocomplete,
    LxTextHighlight
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    path: {
      type: String,
      default: 'base'
    },
    isDisabled: {
      type: Boolean
    },
    isImport: {
      type: Boolean,
      default: false
    },
    addSelectedItem: {
      type: Function
    },
    deleteSelectedItem: {
      type: Function
    },
    validationRef: {
      type: Object,
      default: () => ({})
    },
    patientId: String,
    diagnoses: {
      type: Array,
      default: () => ([])
    }
  },
  data () {
    return {
      model: {
        base: {}
      },
      selectedIndexICD10: null,
      Primaertumor_ICD_VersionTypes: null,
      resultSearchModel: {},
      hideTooltips: false,
      sideLocalizations: []
    }
  },
  created () {
    this.Primaertumor_ICD_VersionTypes = this.filterVersions('icd10')
    this.sideLocalizations = this.formsMetaData.SeitenlokalisationTypesShort
  },
  methods: {
    /**
     * Add empty agent to agent array after clicking 'add'
     */
    addCauseOfDeath () {
      if (!this.model[this.path].todesursachen) {
        this.$set(this.model[this.path], 'todesursachen', [])
      }

      this.$set(this.model[this.path].todesursachen, this.model[this.path].todesursachen.length, {})
    },

    /**
     * Set register_form_id to death form after selection of linked diagnosis
     *
     * @param subjId The selected subj_id
     */
    setRegisterFormId (subjId) {
      const selectedDiagnosis = this.diagnoses.find(elem => elem.subj_id === subjId)

      if (selectedDiagnosis && selectedDiagnosis.diag_id) {
        this.model.register_form_id = selectedDiagnosis.diag_id
      }
    },

    /**
     * Delete ICD10-code and text after changing version
     *
     * @param index The index of disease-element
     */
    resetICD10Code (index) {
      this.$delete(this.model[this.path].todesursachen[index], 'code')
      this.$delete(this.model[this.path].todesursachen[index], 'description')
    },

    /**
     * Format text of diagnoses in selection to show user more than subj_id
     *
     * @param item The diagnosis object
     * @returns {string}
     */
    formatTextOfDiagnosisSelection (item) {
      let text = item.diag_code + ' (' + this.$options.filters['formatDate'](item.diag_date, 'DD.MM.YYYY')

      if (item.localization && item.localization && this.sideLocalizations[item.localization]) {
        text += ' - ' + this.sideLocalizations[item.localization]
      }

      text += ')'

      return text
    },

    /**
     * Delete selected element of death causes and trigger validator for saving
     */
    deleteItem (path, index, field) {
      this.deleteSelectedItem(path, index)
      this.validationRef.fields[field].changed = true
    },

    /**
     * Set value of tod_tumorbedingt in dependency to date
     *
     *  @param date The date of death
     */
    setDependencyToDate (date) {
      // overwrite value only if there is no date or value is empty
      if (!this.model.tod_tumorbedingt || !date) {
        this.$set(this.model, 'tod_tumorbedingt', date ? 'J' : null)
      }
    },

    /**
     * Get list of icd10 codes via regex in auto-complete
     *
     * @param data The search string
     * @param index The index of selected item
     */
    getDataForAutocomplete (data, index) {
      // clear string from special characters to provide errors
      data = this.clearStringForRegexSearch(data)

      let config = {
        opts: {
          limit: 30
        },
        query: {
          searchValue: data
        }
      }

      // if icd-10 version is set, send it to backend
      const foundVersion = this.Primaertumor_ICD_VersionTypes.find(elem => elem.value === this.model[this.path].todesursachen[index].version)

      if (foundVersion?.misdValue) {
        config.query.version = foundVersion.misdValue
      }

      this.$http.get('/icd10?config=' + JSON.stringify(config))
        .then(res => {
          this.resultSearchModel = {
            result: res.data.items,
            count: res.data.count,
            version: res.data.version,
            index: index
          }
        })
        .catch(this.NotifyError)
    },

    /**
     * Set icd10 value via list form auto complete
     *
     * @param data The object of selected element in result list
     * @param index The index of selected item
     */
    setDiagnosis (data, index) {
      if (data && data.code) {
        const foundVersion = this.Primaertumor_ICD_VersionTypes.find(elem => elem.misdValue === this.resultSearchModel.version)
        const version = foundVersion?.value ? foundVersion?.value : this.formsMetaData.ICD10_Version

        this.$set(this.model[this.path].todesursachen[index], 'code', data.code)
        this.$set(this.model[this.path].todesursachen[index], 'description', data.description)
        this.$set(this.model[this.path].todesursachen[index], 'version', version)
      } else {
        if (!this.model[this.path].todesursachen[index].code || typeof data === 'string' || (this.validationRef.errors  && this.validationRef.errors[this.path + '.todesursachen.' + index + '.code'] && this.validationRef.errors[this.path + '.todesursachen.' + index + '.code'].length > 0)) {
          // nextTick is necessary to trigger watcher in autocomplete on the right way
          this.$nextTick(() => {
            // delete input of icd10, if not selected from result list
            if (typeof data === 'string') this.$set(this.model[this.path].todesursachen[index], 'code', null)
          })

          this.$set(this.model[this.path].todesursachen[index], 'description', undefined)
          this.$set(this.model[this.path].todesursachen[index], 'version', undefined)
        }
      }
    }
  },
  watch: {
    model: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.$emit('input', newValue)
        }
      },
      immediate: true
    },
    value: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.model = newValue
          this.model[this.path] = this.model[this.path] || { todesursachen: [] }
        }
      },
      immediate: true
    }
  }
}
</script>
